/**
 * Notices when the machine or the network came back, and tells the socket to
 * make sure it is really alive.
 *
 * No single browser signal detects a laptop lid-open. `online` fires when the
 * interface gets an address — which a captive portal will happily lie about —
 * and stays silent if the interface never dropped. `visibilitychange` misses a
 * sleep that left the tab visible. The watchdog is the one that actually
 * catches a suspend, because it needs no OS event to reach the page: it just
 * notices that the wall clock jumped. So treat them all as hints, funnel them
 * into one idempotent callback, and let the socket decide what to do.
 *
 * Mirrored from chat-frontend/client/utils/socketWakeSignals.ts. This is the
 * third copy (chat-frontend, opal-desktop's socket, and here), which is past
 * the point where mirroring is comfortable — but the shared-package route runs
 * through common-libs, which publishes to GAR under changesets, so it is a
 * release-gated change rather than a refactor. Extract it when that release can
 * be sequenced; until then keep the copies in step by hand.
 */

export type WakeReason = 'online' | 'pageshow' | 'resume' | 'suspend' | 'visible';

export type WakeTarget = {
  isConnected: () => boolean;
  /**
   * Tear the connection down and build a new one. Used when the socket claims
   * to be connected but the transport underneath it is probably dead.
   */
  cycle: () => void;
  /**
   * Reconnect a socket that already knows it is down. `staleAuth` means the gap
   * was long enough that the access token has probably expired.
   */
  revive: (staleAuth: boolean) => void;
};

/**
 * The watchdog ticks at 1s and calls a gap this size a suspend. Chrome throttles
 * background timers to about one tick a minute, so a hidden tab can trip this
 * without the machine ever sleeping. That is fine — everything it triggers is
 * idempotent, and a false positive is far cheaper than the missed wake this
 * exists to catch.
 */
export const SUSPEND_GAP_MS = 30_000;
const WATCHDOG_TICK_MS = 1000;

/** Spread event-driven reconnects: an ISP blip fires `online` for everyone at once. */
const RECONNECT_JITTER_MS = 3000;

/** Several wake signals fire together; collapse them into one attempt. */
const DEBOUNCE_MS = 250;

/**
 * Pick the reason that matters more when several collapse into one debounced
 * wake. `suspend` is the only reason that unconditionally forces a cycle (see
 * `hadSignificantGap`), so it wins any collision; otherwise the first reason in
 * is kept, preserving the original behaviour for the state-dependent reasons.
 */
function mostSignificantReason(current: WakeReason | null, next: WakeReason): WakeReason {
  if (current === null || current === 'suspend') {
    return current ?? next;
  }
  return next === 'suspend' ? next : current;
}

export class SocketWakeSignals {
  private bound = false;
  private watchdogTimer: ReturnType<typeof setInterval> | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private watchdogLastTick = 0;
  private lastDisconnectedAt: number | null = null;
  private sawOffline = false;
  private pendingReason: WakeReason | null = null;
  private pendingImmediate = false;

  constructor(
    private readonly target: WakeTarget,
    private readonly random: () => number = Math.random
  ) {}

  /**
   * Idempotent, and bound once per service. The chat mounts as a federated
   * component that can mount more than once, and duplicate listeners would
   * multiply every reconnect attempt.
   */
  bind(): void {
    if (this.bound || typeof window === 'undefined') {
      return;
    }
    this.bound = true;

    window.addEventListener('online', () => this.trigger('online'));
    window.addEventListener('offline', () => {
      // Remember it even if socket.io has not noticed yet: this is what tells
      // the next wake that the gap was real and the socket needs cycling.
      this.sawOffline = true;
    });
    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
      if (event.persisted) {
        this.trigger('pageshow');
      }
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.trigger('visible');
      }
    });
    document.addEventListener('resume', () => this.trigger('resume'));

    this.watchdogLastTick = Date.now();
    this.watchdogTimer = setInterval(() => {
      const now = Date.now();
      const drift = now - this.watchdogLastTick - WATCHDOG_TICK_MS;
      this.watchdogLastTick = now;
      if (drift >= SUSPEND_GAP_MS) {
        this.trigger('suspend');
      }
    }, WATCHDOG_TICK_MS);
  }

  noteConnected(): void {
    this.lastDisconnectedAt = null;
    this.sawOffline = false;
  }

  noteDisconnected(): void {
    this.lastDisconnectedAt = Date.now();
  }

  /** Stop the watchdog and any pending debounce. Mainly so tests don't leak timers. */
  dispose(): void {
    if (this.watchdogTimer !== null) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.pendingReason = null;
    this.pendingImmediate = false;
    this.bound = false;
  }

  /** Exposed for tests and for an explicit "check the connection now". */
  trigger(reason: WakeReason): void {
    // Collapse signals that fire together, but track two things independently:
    // the most significant reason (does the connection deserve distrust) and
    // whether any signal in the burst was a user-driven `visible` (should the
    // reconnect be instant). They are orthogonal — a `suspend` upgrade must not
    // cancel the instant path a co-occurring `visible` should still grant, and
    // keeping only a single winning reason would conflate the two.
    this.pendingReason = mostSignificantReason(this.pendingReason, reason);
    this.pendingImmediate = this.pendingImmediate || reason === 'visible';
    if (this.debounceTimer !== null) {
      return;
    }
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      const pending = this.pendingReason ?? reason;
      const immediate = this.pendingImmediate;
      this.pendingReason = null;
      this.pendingImmediate = false;
      this.run(pending, immediate);
    }, DEBOUNCE_MS);
  }

  private run(reason: WakeReason, immediate: boolean): void {
    const significantGap = this.hadSignificantGap(reason);

    if (this.target.isConnected()) {
      // `connected` is not to be trusted after a suspend: the TCP connection is
      // usually dead while the flag still reads true, and engine.io needs
      // pingInterval + pingTimeout (45s) to work that out. Cycle rather than
      // wait — but only on a real gap, because a tab focus fires far too often.
      if (significantGap) {
        this.target.cycle();
      }
      return;
    }

    // Jitter so an outage that dropped every client doesn't bring them all back
    // at once. A visibility wake is one person opening one tab, so it skips the
    // jitter and feels instant — even when it collapsed together with a
    // herd-style signal like `online` or `suspend`.
    const delay = immediate ? 0 : Math.round(this.random() * RECONNECT_JITTER_MS);
    setTimeout(() => {
      if (this.target.isConnected()) {
        return;
      }
      this.target.revive(significantGap);
    }, delay);
  }

  /**
   * Whether enough happened to justify distrusting the connection. A watchdog
   * suspend or an observed `offline` both mean the transport was probably
   * severed underneath us; a plain tab focus does not.
   */
  private hadSignificantGap(reason: WakeReason): boolean {
    if (reason === 'suspend' || this.sawOffline) {
      return true;
    }
    if (this.lastDisconnectedAt === null) {
      return false;
    }
    return Date.now() - this.lastDisconnectedAt >= SUSPEND_GAP_MS;
  }
}
